<template>
  <span v-if="visible" class="inline-flex">
    <Button
      ref="btnRef"
      type="button"
      severity="secondary"
      text
      size="small"
      :aria-label="t('tenant_switcher.open_menu_aria')"
      v-tooltip.bottom="t('shared.switch_company')"
      @click="toggleMenu"
    >
      <span :class="['flex items-center gap-2', 'max-w-[180px]']">
        <img v-if="currentTenant?.logo_url" :src="currentTenant.logo_url" :alt="currentLabel"
          class="h-4 w-4 object-contain rounded-sm" />
        <i v-else class="pi pi-building text-xs" />
        <!-- MYAC-1393 — icon-only on phones so the header fits; the button's
             aria-label and tooltip still say what it does. MYAC-1427 — the top
             header kept it icon-only below 1280px and truncated a long name
             below 1536px; MYAC-1418 — icon-only up to 1535px, since
             « Travail en cours » leaves no room for a name beside the menu. -->
        <span :class="['hidden truncate text-sm', compactUntil2xl ? '2xl:inline' : 'sm:inline']">{{ currentLabel }}</span>
        <i class="pi pi-angle-down text-xs" />
      </span>
    </Button>
    <Menu ref="menuRef" :model="menuItems" :popup="true">
      <template #item="{ item, props }">
        <a v-ripple class="flex items-center w-full" v-bind="props.action" @click="onItemClick(item as SwitcherItem)">
          <span class="flex-1 truncate" :title="String(item.label ?? '')">{{ item.label }}</span>
          <i
            v-if="(item as SwitcherItem).isCurrent"
            class="pi pi-check text-xs ml-2"
            :aria-label="t('tenant_switcher.current_marker')"
          />
        </a>
      </template>
    </Menu>
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import { useTenantsStore } from '@/stores/tenants'
import { logger } from '@/utils/logger'

const props = withDefaults(
  defineProps<{
    // Destination after a successful switch; null disables navigation so the
    // host page can react to the tenant change itself (e.g. ResubscribeView)
    redirectTo?: RouteLocationRaw | null
    // The top header's tighter rule: no company name below 1536px (MYAC-1427, MYAC-1418)
    compactUntil2xl?: boolean
  }>(),
  { redirectTo: () => ({ name: 'dashboard' }) },
)

const { t } = useI18n()
const router = useRouter()
const tenantsStore = useTenantsStore()
const { tenants, currentTenant, currentTenantId } = storeToRefs(tenantsStore)

const menuRef = ref<{ toggle: (e: Event) => void } | null>(null)
const btnRef = ref<{ $el?: HTMLElement } | null>(null)
const switching = ref(false)

const visible = computed(() => (tenants.value?.length || 0) >= 2)
const currentLabel = computed(() => currentTenant.value?.name || t('tenant_switcher.current_label'))

interface SwitcherItem {
  label: string
  tenantId: string
  isCurrent: boolean
}

const sortedTenants = computed(() => {
  const list = [...(tenants.value || [])]
  return list.sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
  )
})

const menuItems = computed<SwitcherItem[]>(() =>
  sortedTenants.value.map((t) => ({
    label: t.name || '-',
    tenantId: t.id,
    isCurrent: t.id === currentTenantId.value,
  })),
)

function toggleMenu(event: Event) {
  menuRef.value?.toggle(event)
}

async function onItemClick(item: SwitcherItem) {
  if (switching.value) return
  if (!item.tenantId || item.isCurrent) return
  switching.value = true
  try {
    await tenantsStore.switchTenant(item.tenantId)
    if (props.redirectTo) await router.push(props.redirectTo)
  } catch (e) {
    logger.error('TenantSwitcher: switch failed', e)
  } finally {
    switching.value = false
  }
}
</script>
